import { useTranslation } from 'next-i18next';
import { useRouter, useSearchParams } from 'next/navigation';
import { useCallback, useMemo } from 'react';
import { Box, ButtonV2, IconChevron, ProgressBar, colors } from '@bridebook/ui';
import { ProgressBarMarker } from '@bridebook/ui/src/icons';
import { IconCross } from '@bridebook/ui/src/icons/dynamic';
import { RSVP_FORM_STEPS } from '@bridebook/web/lib/rsvp/constants';
import getWebsiteUrl from '@bridebook/web/lib/rsvp/utils/get-website-url';
import { useRsvpNavigation, useRsvpForms, useRsvpState, useRsvpData } from '../guest-rsvp.context';
import componentStyles from './topbar.style';
import { getThemeProperties } from '@bridebook/web/lib/wedding-website/utils';

const STEPS_WITH_BACK_BUTTON = ['RSVP_FORM.QUESTIONS', 'RSVP_FORM.NOTE', 'RSVP_FORM.SUMMARY'];
const STEPS_WITH_PROGRESS_BAR = [
  'NAME_FORM',
  'RSVP_FORM.EVENTS',
  'RSVP_FORM.QUESTIONS',
  'RSVP_FORM.NOTE',
  'RSVP_FORM.SUMMARY',
  'RSVP_FORM.CONFIRMATION',
];

const GuestRsvpTopbar: React.FC = () => {
  const { t } = useTranslation('weddingWebsite');
  const router = useRouter();
  const searchParams = useSearchParams();

  const { step, setStep, currentQuestionIndex, setCurrentQuestionIndex, onClose } =
    useRsvpNavigation();
  const { questionsForm } = useRsvpForms();
  const { websiteId, isRsvpOpen, isPreview } = useRsvpState();
  const { theme } = useRsvpData();

  const themeProperties = useMemo(() => getThemeProperties(theme), [theme]);

  const styles = componentStyles(themeProperties);

  const questionsFormValues = questionsForm.getValues();

  const numberOfQuestions = useMemo(
    () => Object.keys(questionsFormValues).length - 1,
    [questionsFormValues],
  );

  const numberOfProgressSteps = numberOfQuestions + 3;

  const showBackButton = useMemo(() => {
    if (!STEPS_WITH_BACK_BUTTON.includes(step)) return false;
    if (step === 'RSVP_FORM.SUMMARY' && !isRsvpOpen) return false;
    return true;
  }, [step, isRsvpOpen]);

  const showProgressBar = useMemo(() => {
    if (!STEPS_WITH_PROGRESS_BAR.includes(step)) return false;
    if (step === 'RSVP_FORM.SUMMARY' && !isRsvpOpen) return false;
    return true;
  }, [step, isRsvpOpen]);

  const handleBack = useCallback(() => {
    if (step === 'RSVP_FORM.QUESTIONS' && currentQuestionIndex > 0) {
      setCurrentQuestionIndex(currentQuestionIndex - 1);
      return;
    }
    if (step === 'RSVP_FORM.QUESTIONS' && currentQuestionIndex === 0) {
      // If on the first question, go back to previous step
      setStep(
        RSVP_FORM_STEPS[
          Math.max(0, RSVP_FORM_STEPS.findIndex((s) => s === 'RSVP_FORM.QUESTIONS') - 1)
        ],
      );
      return;
    }

    const previousStepIndex = RSVP_FORM_STEPS.findIndex((s) => s === step) - 1;

    if (previousStepIndex >= 0) {
      // If the previous step is 'RSVP_FORM.QUESTIONS' and there are no questions, skip to the step before that
      if (RSVP_FORM_STEPS[previousStepIndex] === 'RSVP_FORM.QUESTIONS' && numberOfQuestions === 0) {
        setStep(RSVP_FORM_STEPS[previousStepIndex - 1]);
        return;
      }

      setStep(RSVP_FORM_STEPS[previousStepIndex]);
      return;
    }
  }, [step, currentQuestionIndex, setStep, setCurrentQuestionIndex, numberOfQuestions]);

  const handleClose = useCallback(() => {
    if (isPreview) {
      onClose?.();
      return;
    }
    router.push(getWebsiteUrl(websiteId, searchParams ?? undefined));
  }, [isPreview, onClose, router, searchParams, websiteId]);

  // Calculate progress based on step
  const currentProgress = useMemo(() => {
    switch (step) {
      case 'RSVP_FORM.EVENTS':
        return 0;
      case 'RSVP_FORM.QUESTIONS':
        return ((1 + currentQuestionIndex) / numberOfProgressSteps) * 100;
      case 'RSVP_FORM.NOTE':
        return ((1 + numberOfQuestions) / numberOfProgressSteps) * 100;
      case 'RSVP_FORM.SUMMARY':
        return ((2 + numberOfQuestions) / numberOfProgressSteps) * 100;
      case 'RSVP_FORM.CONFIRMATION':
        return 100;
      default:
        return 0;
    }
  }, [step, currentQuestionIndex, numberOfQuestions, numberOfProgressSteps]);

  return (
    <Box style={styles.container}>
      <Box style={styles.header}>
        {showBackButton ? (
          <ButtonV2
            onClick={handleBack}
            variant="secondary"
            startIcon={<IconChevron style={styles.backButtonIcon} width={12} />}
            size="small"
          />
        ) : (
          <Box w={32} />
        )}
        <Box as="h2" style={styles.title}>
          {t('rsvp.guest.topbar.title')}
        </Box>
        <ButtonV2
          onClick={handleClose}
          variant="secondary"
          startIcon={<IconCross width={12} />}
          size="small"
        />
      </Box>
      {showProgressBar && (
        <Box style={styles.progressBarContainer}>
          <ProgressBar
            showProgressMarker
            renderProgressMarker={() => <ProgressBarMarker style={styles.progressMarker} />}
            progress={currentProgress}
            height={8}
            maxProgress={100}
            color={colors.indigoCrush}
            backgroundColor="lightGrey"
          />
        </Box>
      )}
    </Box>
  );
};
export default GuestRsvpTopbar;
